import { Box, TextLink, Typography } from "@drone-js/core";
import { useKeyDownHandler } from "apps/commons/hooks/useKeyDownHandler";
import { urlInputBinder } from "libs/analytics/binders/propertySelection/urlInput";
import { useAnalytics } from "libs/analytics/useAnalytics";
import { type FunctionComponent, useState } from "react";
import { useLocation } from "react-router";
import { Footer } from "../Footer";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { InputURLImage } from "./InputURLImage";
import { LearnMoreModal } from "./LearnMoreModal";
import { NewURLInput } from "./NewURLInput";
import { ToastErrorNotification } from "./ToastErrorNotification";
import { useURLValidationHandler } from "./useURLValidationHandler";

export const UrlInputPage: FunctionComponent = () => {
    const {
        inputUrlCms: { learnMore, back, next, importListingPageTitle, importListingUrlInputText },
    } = usePropertySelectionViewModelValue();

    const [isOpen, setIsOpen] = useState(false);
    const validationHandler = useURLValidationHandler();

    const { search } = useLocation();

    useKeyDownHandler(
        (e) => {
            if (e.key === "Enter") validationHandler.onNext();
        },
        [validationHandler.onNext],
    );

    useAnalytics(urlInputBinder);

    const renderLandingToastErrorNotification = () => {
        const errorLists = ["importSelection", "bcomError"];
        const error = new URLSearchParams(search).get("error");
        const isOpen = errorLists.includes(error);

        return <ToastErrorNotification isOpen={isOpen} />;
    };

    return (
        <Box py="32" display="flex" flexDirection="column">
            <Typography variant="heading-xl" mb="32" textColor="base.default">
                {importListingPageTitle}
            </Typography>
            <Typography variant="body-default" mb="4" me="4" textColor="base.default">
                {importListingUrlInputText}
            </Typography>

            <NewURLInput {...validationHandler} onLearnMore={() => setIsOpen(true)} />

            <InputURLImage />

            <Box pt="8" qM={{ pt: "16" }}>
                <TextLink
                    hierarchy="primary"
                    label={learnMore}
                    onClick={() => setIsOpen(true)}
                    data-element-name="learn-more-button"
                />
            </Box>
            {renderLandingToastErrorNotification()}
            <Footer
                secondary={{
                    label: back,
                    dataElementName: "back",
                }}
                primary={{
                    label: next,
                    loading: validationHandler.isLoading,
                    dataElementName: "next",
                }}
                onSecondary={validationHandler.onBack}
                onPrimary={validationHandler.onNext}
            />
            <LearnMoreModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
        </Box>
    );
};
